<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { TrackRecordImportResponse } from '@/types/services/mandate/trackRecord.type.ts'

const props = defineProps<{
  result: TrackRecordImportResponse | null
  error: string | null
}>()

const emit = defineEmits<{
  (e: 'correct'): void
  (e: 'newImport'): void
}>()

const { t } = useI18n()

const ERROR_MESSAGES: Record<string, string> = {
  track_record_mandate_not_found_error_key: t('views.trackRecord.errors.track_record_mandate_not_found_error_key'),
  track_record_parse_error_key: t('views.trackRecord.errors.track_record_parse_error_key'),
  track_record_missing_columns_error_key: t('views.trackRecord.errors.track_record_missing_columns_error_key'),
  track_record_validation_error_key: t('views.trackRecord.errors.track_record_validation_error_key'),
  track_record_invalid_perf_type_error_key: t('views.trackRecord.errors.track_record_invalid_perf_type_error_key'),
  track_record_invalid_source_error_key: t('views.trackRecord.errors.track_record_invalid_source_error_key'),
}

function translateError(raw: string | null): string {
  if (!raw) return t('views.trackRecord.errors.generic')
  return ERROR_MESSAGES[raw] ?? t('views.trackRecord.errors.generic')
}
</script>

<template>
  <v-row>
    <!-- Success -->
    <template v-if="result && !error">
      <v-col cols="12" class="d-flex flex-column align-center py-6">
        <v-icon size="56" icon="PhCheckCircle" color="success" class="mb-3" />
        <p class="text-h6 mb-1">{{ t('views.trackRecord.result.successTitle') }}</p>
        <p class="text-body-2 text-medium-emphasis">
          {{ t('views.trackRecord.result.successMessage', { inserted: result.inserted }) }}
        </p>
      </v-col>

      <v-col v-if="result.warnings.length" cols="12">
        <v-alert type="warning" density="compact">
          <p class="font-weight-medium mb-1">{{ t('views.trackRecord.result.warningsTitle') }}</p>
          <ul class="pl-4">
            <li v-for="(w, i) in result.warnings" :key="i" class="text-body-2">{{ w }}</li>
          </ul>
        </v-alert>
      </v-col>

      <v-col cols="12" class="d-flex justify-end">
        <v-btn variant="outlined" @click="emit('newImport')">
          {{ t('views.trackRecord.result.newImport') }}
        </v-btn>
      </v-col>
    </template>

    <!-- Error -->
    <template v-else>
      <v-col cols="12" class="d-flex flex-column align-center py-6">
        <v-icon size="56" icon="PhXCircle" color="error" class="mb-3" />
        <p class="text-h6 mb-2">{{ t('views.trackRecord.result.errorTitle') }}</p>
        <v-alert
          type="error"
          density="compact"
          class="w-100 text-left"
          :text="translateError(error)"
        />
      </v-col>

      <v-col cols="12" class="d-flex justify-end">
        <v-btn color="primary" @click="emit('correct')">
          <template #prepend>
            <v-icon icon="PhArrowLeft" />
          </template>
          {{ t('views.trackRecord.result.correctButton') }}
        </v-btn>
      </v-col>
    </template>
  </v-row>
</template>
